import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Skeleton, IconButton, Select, MenuItem,
  FormControl, Tooltip, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { keyframes } from '@mui/system';
import { TickerLink } from './TickerActionMenu';
import InfoPopover from './InfoPopover';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getEnhancedEarnings,
  downloadEarningsPdf,
  EnhancedEarningsData,
  UpcomingEarningsEntry,
  RecentEarningsEntry,
  EarningsBeatRate,
} from '../services/api';

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

// Region → country code sets
const REGION_COUNTRIES: Record<string, Set<string>> = {
  US:  new Set(['US']),
  UK:  new Set(['GB', 'UK']),
  EU:  new Set(['DE','FR','NL','IT','ES','BE','PT','AT','IE','FI','SE','DK','NO','CH','LU','PL','GR','CZ','HU','RO']),
  APAC: new Set(['JP','CN','HK','TW','KR','SG','AU','NZ','IN','TH','ID','MY','PH','VN']),
};

type Region = 'US' | 'UK' | 'EU' | 'APAC' | 'All';
const REGION_LABELS: Region[] = ['US', 'UK', 'EU', 'APAC', 'All'];

function inRegion(country: string, region: Region): boolean {
  if (region === 'All') return true;
  return REGION_COUNTRIES[region]?.has(country) ?? false;
}

function matchesRegions(country: string, active: Set<Region>): boolean {
  if (active.has('All')) return true;
  return Array.from(active).some(r => inRegion(country, r));
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CHF: 'CHF ', JPY: '¥',
  CAD: 'CA$', AUD: 'A$', HKD: 'HK$', SEK: 'kr', NOK: 'kr',
  DKK: 'kr', SGD: 'S$', KRW: '₩', INR: '₹', BRL: 'R$',
};

const formatMarketCap = (mc: number | null | undefined, currency = 'USD'): string => {
  if (mc == null) return '—';
  const sym = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
  if (mc >= 1e12) return `${sym}${(mc / 1e12).toFixed(1)}T`;
  if (mc >= 1e9) return `${sym}${(mc / 1e9).toFixed(1)}B`;
  if (mc >= 1e6) return `${sym}${(mc / 1e6).toFixed(0)}M`;
  return `${sym}${mc.toLocaleString()}`;
};

const formatDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const isToday = (iso: string): boolean => {
  const today = new Date();
  const d = new Date(iso + 'T00:00:00');
  return d.toDateString() === today.toDateString();
};

const formatEps = (v: number | null): string => {
  if (v == null) return '--';
  return `$${v.toFixed(2)}`;
};

const formatRevenue = (v: number | null): string => {
  if (v == null) return '--';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toLocaleString()}`;
};

const formatPct = (v: number | null, showPlus = true): string => {
  if (v == null) return '--';
  const sign = v > 0 && showPlus ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};

const pctColor = (v: number | null): string => {
  if (v == null) return bloomberg.text.muted;
  return v >= 0 ? '#4caf50' : '#ef5350';
};

const beatRateColor = (rate: EarningsBeatRate | null): string => {
  if (!rate || rate.total === 0) return bloomberg.text.muted;
  const pct = rate.beats / rate.total;
  return pct >= 0.75 ? '#4caf50' : pct >= 0.5 ? bloomberg.accent.orange : '#ef5350';
};

const BeatMissBadge: React.FC<{ beat: boolean | null; surprisePct: number | null }> = ({ beat, surprisePct }) => {
  if (beat === null || beat === undefined) return <span style={{ color: bloomberg.text.muted, fontSize: bloomberg.fontSize.caption }}>--</span>;
  const color = beat ? '#4caf50' : '#ef5350';
  const bg = beat ? 'rgba(76,175,80,0.15)' : 'rgba(239,83,80,0.15)';
  const label = beat ? 'BEAT' : 'MISS';
  const pct = surprisePct != null ? ` ${surprisePct > 0 ? '+' : ''}${surprisePct.toFixed(1)}%` : '';
  const isOutlier = surprisePct != null && Math.abs(surprisePct) > 50;
  const chip = (
    <Chip label={`${label}${pct}${isOutlier ? ' ⚠' : ''}`} size="small" sx={{
      height: 18, fontSize: bloomberg.fontSize.micro, fontWeight: 700,
      bgcolor: bg, color: color, borderRadius: '4px',
      '& .MuiChip-label': { px: 0.8 },
    }} />
  );
  if (isOutlier) {
    return (
      <Tooltip title="Large surprise — GAAP EPS may include one-time items not reflected in analyst estimates." arrow>
        {chip}
      </Tooltip>
    );
  }
  return chip;
};

const BeatRateDisplay: React.FC<{ rate: EarningsBeatRate | null }> = ({ rate }) => {
  if (!rate) return <span style={{ color: bloomberg.text.muted, fontSize: bloomberg.fontSize.caption }}>--</span>;
  const color = beatRateColor(rate);
  return (
    <Tooltip title={`Beat estimates ${rate.beats} out of ${rate.total} recent quarters`}>
      <span style={{ color, fontSize: bloomberg.fontSize.bodyDense, fontWeight: 700, cursor: 'default' }}>
        {rate.rate_str}
      </span>
    </Tooltip>
  );
};

const RegionChips: React.FC<{
  active: Set<Region>;
  onChange: (r: Set<Region>) => void;
  size?: 'small' | 'xs';
}> = ({ active, onChange, size = 'small' }) => {
  const toggle = (r: Region) => {
    if (r === 'All') {
      onChange(new Set<Region>(['All']));
      return;
    }
    const next = new Set(active);
    next.delete('All');
    if (next.has(r)) {
      next.delete(r);
      if (next.size === 0) next.add('All');
    } else {
      next.add(r);
    }
    onChange(next);
  };
  const fs = size === 'xs' ? bloomberg.fontSize.micro : bloomberg.fontSize.caption;
  const h = size === 'xs' ? 20 : 22;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {REGION_LABELS.map(r => {
        const on = active.has(r) || (r !== 'All' && active.has('All') && false);
        const isActive = active.has(r);
        return (
          <Chip
            key={r}
            label={r}
            size="small"
            clickable
            onClick={() => toggle(r)}
            sx={{
              height: h, fontSize: fs, fontWeight: isActive ? 700 : 400,
              bgcolor: isActive ? bloomberg.accent.orange : 'transparent',
              color: isActive ? '#000' : bloomberg.text.secondary,
              border: `1px solid ${isActive ? bloomberg.accent.orange : bloomberg.border}`,
              '&:hover': { bgcolor: isActive ? bloomberg.accent.orange : 'rgba(255,167,38,0.12)' },
            }}
          />
        );
      })}
    </Box>
  );
};

const PAGE_SIZE = 50;
const DEFAULT_REGIONS: Set<Region> = new Set<Region>(['US', 'UK', 'EU']);

const UpcomingEarningsCalendar: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [data, setData] = useState<EnhancedEarningsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [days, setDays] = useState(compact ? 7 : 14);
  const [activeRegions, setActiveRegions] = useState<Set<Region>>(DEFAULT_REGIONS);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [recentPage, setRecentPage] = useState(1);

  // Hover-to-preview, click-to-pin detail popover (compact view)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSymbol, setActiveSymbol] = useState<string | null>(null);
  const [pinned, setPinned] = useState(false);

  const handleEnter = (e: React.MouseEvent<HTMLElement>, symbol: string) => {
    if (pinned) return;
    setAnchorEl(e.currentTarget);
    setActiveSymbol(symbol);
  };
  const handleLeave = () => {
    if (!pinned) {
      setAnchorEl(null);
      setActiveSymbol(null);
    }
  };
  const handleRowClick = (e: React.MouseEvent<HTMLElement>, symbol: string) => {
    if (pinned && activeSymbol === symbol) {
      setPinned(false);
      setAnchorEl(null);
      setActiveSymbol(null);
    } else {
      setPinned(true);
      setAnchorEl(e.currentTarget);
      setActiveSymbol(symbol);
    }
  };
  const handlePopoverClose = () => {
    setPinned(false);
    setAnchorEl(null);
    setActiveSymbol(null);
  };

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    try {
      const daysBack = compact ? 0 : days === 1 ? 0 : days === 0 ? 30 : 14;
      const result = await getEnhancedEarnings(days, daysBack);
      setData(result);
      setUpcomingPage(1);
      setRecentPage(1);
    } catch (err) {
      console.error('Error fetching enhanced earnings:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days, compact]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filterByRegion = useCallback(<T extends { country?: string }>(items: T[]): T[] => {
    if (activeRegions.has('All')) return items;
    return items.filter(e => matchesRegions((e as any).country || '', activeRegions));
  }, [activeRegions]);

  const filteredUpcoming = useMemo((): UpcomingEarningsEntry[] => filterByRegion(data?.upcoming ?? []) as UpcomingEarningsEntry[], [data, filterByRegion]);
  const filteredRecent = useMemo((): RecentEarningsEntry[] => filterByRegion(data?.recent ?? []) as RecentEarningsEntry[], [data, filterByRegion]);

  const thSx = {
    textAlign: 'left' as const, py: 0.8, px: 1, fontSize: 11, fontWeight: 600,
    color: bloomberg.accent.orange, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
  };
  const thSxRight = { ...thSx, textAlign: 'right' as const };
  const tdSx = { py: 0.6, px: 1, fontSize: 12, color: bloomberg.text.secondary };

  // ── COMPACT DASHBOARD VIEW ──
  if (compact) {
    const COMPACT_PAGE_SIZE = 20;

    // Group by date, sort dates asc, sort within each date by market cap desc
    const grouped: Record<string, UpcomingEarningsEntry[]> = {};
    filteredUpcoming.forEach(e => {
      if (!grouped[e.earnings_date]) grouped[e.earnings_date] = [];
      grouped[e.earnings_date].push(e);
    });
    const sortedDates = Object.keys(grouped).sort();
    sortedDates.forEach(d => grouped[d].sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0)));

    // Flatten to a list of { type: 'header' | 'row', ... } for easy pagination
    type FlatItem =
      | { type: 'header'; date: string; count: number }
      | { type: 'row'; entry: UpcomingEarningsEntry };
    const flat: FlatItem[] = [];
    sortedDates.forEach(d => {
      flat.push({ type: 'header', date: d, count: grouped[d].length });
      grouped[d].forEach(e => flat.push({ type: 'row', entry: e }));
    });

    // Paginate on rows only (headers travel with their rows)
    const allRows = flat.filter(f => f.type === 'row');
    const totalPages = Math.ceil(allRows.length / COMPACT_PAGE_SIZE);
    const pageRowSymbols = new Set(
      (allRows.slice((upcomingPage - 1) * COMPACT_PAGE_SIZE, upcomingPage * COMPACT_PAGE_SIZE) as Extract<FlatItem, { type: 'row' }>[])
        .map(f => f.entry.symbol)
    );
    // Include a date header if at least one of its rows is on this page
    const pageItems = flat.filter(f =>
      f.type === 'header'
        ? grouped[(f as Extract<FlatItem, { type: 'header' }>).date].some(e => pageRowSymbols.has(e.symbol))
        : pageRowSymbols.has((f as Extract<FlatItem, { type: 'row' }>).entry.symbol)
    );

    const startRow = (upcomingPage - 1) * COMPACT_PAGE_SIZE + 1;
    const endRow = Math.min(upcomingPage * COMPACT_PAGE_SIZE, allRows.length);

    return (
      <>
      <Paper elevation={2} sx={{ p: 2, mb: 3, height: 600, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon sx={{ color: bloomberg.accent.orange, fontSize: 18 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
              Upcoming Earnings
            </Typography>
            {data && (
              <Chip
                label={`${filteredUpcoming.length} total`}
                size="small"
                sx={{ height: 18, fontSize: bloomberg.fontSize.micro, bgcolor: 'rgba(255,167,38,0.1)', color: bloomberg.accent.orange }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <RegionChips active={activeRegions} onChange={r => { setActiveRegions(r); setUpcomingPage(1); }} size="xs" />
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Button size="small" disabled={upcomingPage === 1} onClick={() => setUpcomingPage(p => p - 1)}
                  sx={{ minWidth: 24, px: 0.5, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.secondary }}>‹</Button>
                <Typography sx={{ fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted, whiteSpace: 'nowrap' }}>
                  {startRow}–{endRow} of {allRows.length}
                </Typography>
                <Button size="small" disabled={upcomingPage === totalPages} onClick={() => setUpcomingPage(p => p + 1)}
                  sx={{ minWidth: 24, px: 0.5, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.secondary }}>›</Button>
              </Box>
            )}
            <IconButton size="small" onClick={() => fetchData(true)} disabled={refreshing}>
              <RefreshIcon sx={{ fontSize: 16, animation: refreshing ? `${spin} 1s linear infinite` : 'none' }} />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          [1,2,3,4,5].map(i => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
        ) : pageItems.length === 0 ? (
          <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, color: bloomberg.text.muted, textAlign: 'center', py: 2 }}>
            No upcoming earnings for selected regions
          </Typography>
        ) : (
          <Box>
            {pageItems.map((item) => {
              if (item.type === 'header') {
                const d = (item as Extract<FlatItem, { type: 'header' }>).date;
                return (
                  <Box key={`hdr-${d}`} sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 1,
                    bgcolor: isToday(d) ? bloomberg.accent.orangeDim : 'transparent',
                    borderLeft: `3px solid ${isToday(d) ? bloomberg.accent.orange : bloomberg.border.primary}`,
                  }}>
                    <Typography sx={{ fontSize: bloomberg.fontSize.caption, fontWeight: 700, color: isToday(d) ? bloomberg.accent.orange : bloomberg.text.primary, letterSpacing: '0.03em' }}>
                      {formatDate(d)}
                    </Typography>
                  </Box>
                );
              }
              const e = (item as Extract<FlatItem, { type: 'row' }>).entry;
              return (
                <Box
                  key={e.symbol}
                  onMouseEnter={(ev) => handleEnter(ev, e.symbol)}
                  onMouseLeave={handleLeave}
                  onClick={(ev) => handleRowClick(ev, e.symbol)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85, px: 1,
                    borderBottom: `1px solid ${bloomberg.border.primary}`,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: bloomberg.bg.hover },
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: beatRateColor(e.beat_rate), flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: bloomberg.font.mono, fontSize: bloomberg.fontSize.bodyDense, fontWeight: 600, color: bloomberg.text.primary, width: 56, flexShrink: 0 }}>
                    <TickerLink symbol={e.symbol} fontSize={bloomberg.fontSize.bodyDense} />
                  </Typography>
                  <Typography sx={{
                    fontSize: bloomberg.fontSize.bodyDense, color: bloomberg.text.secondary, flex: 1,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {e.company_name}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <InfoPopover anchorEl={anchorEl} onClose={handlePopoverClose}>
        {(() => {
          const e = filteredUpcoming.find(x => x.symbol === activeSymbol);
          if (!e) return null;
          return (
            <Box onMouseEnter={() => setAnchorEl(anchorEl)} onMouseLeave={handleLeave}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                <TickerLink symbol={e.symbol} fontSize={bloomberg.fontSize.bodyDense} />
                <Typography sx={{ fontSize: bloomberg.fontSize.caption, color: bloomberg.text.muted }}>{(e as any).country || ''}</Typography>
              </Box>
              <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, color: bloomberg.text.secondary, mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.company_name}{e.sector ? ` · ${e.sector}` : ''}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mkt Cap</Typography>
                  <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, fontWeight: 600, fontFamily: bloomberg.font.mono, color: bloomberg.text.primary }}>
                    {formatMarketCap(e.market_cap, (e as any).currency)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>EPS Est</Typography>
                  <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, fontWeight: 600, fontFamily: bloomberg.font.mono, color: bloomberg.text.primary }}>
                    {formatEps(e.eps_estimate)}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Beat Rate</Typography>
                  <Box sx={{ fontSize: bloomberg.fontSize.bodyDense }}><BeatRateDisplay rate={e.beat_rate} /></Box>
                </Box>
              </Box>
            </Box>
          );
        })()}
      </InfoPopover>
      </>
    );
  }

  // ── FULL PAGE VIEW ──
  const upcomingGrouped: Record<string, UpcomingEarningsEntry[]> = {};
  filteredUpcoming.forEach(e => {
    if (!upcomingGrouped[e.earnings_date]) upcomingGrouped[e.earnings_date] = [];
    upcomingGrouped[e.earnings_date].push(e);
  });
  const upcomingSortedDates = Object.keys(upcomingGrouped).sort();

  const upcomingTotalPages = Math.ceil(filteredUpcoming.length / PAGE_SIZE);
  const upcomingPageSlice = new Set(
    filteredUpcoming.slice((upcomingPage - 1) * PAGE_SIZE, upcomingPage * PAGE_SIZE).map(e => e.symbol)
  );
  const upcomingDatesForPage = upcomingSortedDates.filter(d =>
    upcomingGrouped[d].some(e => upcomingPageSlice.has(e.symbol))
  );

  const recentTotalPages = Math.ceil(filteredRecent.length / PAGE_SIZE);
  const recentPageSlice = filteredRecent.slice((recentPage - 1) * PAGE_SIZE, recentPage * PAGE_SIZE);

  const totalCount = filteredUpcoming.length + filteredRecent.length;

  return (
    <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <CalendarMonthIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
            Earnings Calendar
          </Typography>
          {data && filteredRecent.length > 0 && (
            <Chip label={`${filteredRecent.length} recent`} size="small" sx={{
              height: 20, fontSize: 10, fontWeight: 600,
              bgcolor: 'rgba(76,175,80,0.12)', color: '#4caf50',
            }} />
          )}
          {data && filteredUpcoming.length > 0 && (
            <Chip label={`${filteredUpcoming.length} upcoming`} size="small" sx={{
              height: 20, fontSize: 10, fontWeight: 600,
              bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange,
            }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <RegionChips active={activeRegions} onChange={r => { setActiveRegions(r); setUpcomingPage(1); setRecentPage(1); }} />
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              value={days}
              onChange={(e) => setDays(e.target.value as number)}
              sx={{
                height: 28, fontSize: 11, color: bloomberg.text.secondary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: bloomberg.border },
                '& .MuiSvgIcon-root': { color: bloomberg.text.muted, fontSize: 16 },
              }}
            >
              <MenuItem value={1}>1 day</MenuItem>
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={0}>All upcoming</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Download Weekly Earnings PDF">
            <span>
              <IconButton
                onClick={async () => {
                  setPdfLoading(true);
                  try { await downloadEarningsPdf('all', 7, 7); }
                  catch (e) { console.error(e); }
                  finally { setPdfLoading(false); }
                }}
                disabled={pdfLoading}
                size="small"
                sx={{ color: bloomberg.text.muted }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 18, ...(pdfLoading ? { animation: `${spin} 1s linear infinite` } : {}) }} />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={() => fetchData(true)} disabled={refreshing} size="small" sx={{ color: bloomberg.text.muted }}>
            <RefreshIcon sx={{ fontSize: 18, ...(refreshing ? { animation: `${spin} 1s linear infinite` } : {}) }} />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={32} />)}
        </Box>
      ) : totalCount === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No earnings found for the selected regions and timeframe.
        </Typography>
      ) : (
        <>
          {/* ── RECENT RESULTS ── */}
          {filteredRecent.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Recent Results ({filteredRecent.length})
                </Typography>
                {recentTotalPages > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Button size="small" disabled={recentPage === 1} onClick={() => setRecentPage(p => p - 1)}
                      sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>‹</Button>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>{recentPage}/{recentTotalPages}</Typography>
                    <Button size="small" disabled={recentPage === recentTotalPages} onClick={() => setRecentPage(p => p + 1)}
                      sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>›</Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ borderBottom: `2px solid #4caf50`, bgcolor: bloomberg.bg.secondary }}>
                      <Box component="th" sx={thSx}>Symbol</Box>
                      <Box component="th" sx={thSx}>Company</Box>
                      <Box component="th" sx={thSx}>Date</Box>
                      <Box component="th" sx={thSx}>Country</Box>
                      <Box component="th" sx={thSx}>EPS Est / Act</Box>
                      <Box component="th" sx={thSx}>Beat/Miss</Box>
                      <Box component="th" sx={thSxRight}>Post-Earn Move</Box>
                      <Box component="th" sx={{ ...thSx, textAlign: 'center' as const }}>Beat Rate</Box>
                      <Box component="th" sx={thSxRight}>Mkt Cap</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {(recentPageSlice as RecentEarningsEntry[]).map((e) => (
                      <Box component="tr" key={e.symbol} sx={{
                        borderBottom: `1px solid ${bloomberg.border}`,
                        '&:hover': { bgcolor: bloomberg.bg.hover },
                      }}>
                        <Box component="td" sx={tdSx}><TickerLink symbol={e.symbol} fontSize={12} /></Box>
                        <Box component="td" sx={{ ...tdSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                          {e.company_name}
                        </Box>
                        <Box component="td" sx={{ ...tdSx, fontSize: 11, color: bloomberg.text.muted, whiteSpace: 'nowrap' }}>
                          {formatDate(e.earnings_date)}
                        </Box>
                        <Box component="td" sx={{ ...tdSx, fontSize: 11 }}>{(e as any).country || '—'}</Box>
                        <Box component="td" sx={tdSx}>
                          {e.revenue_only ? (
                            <span style={{ color: bloomberg.text.muted, fontStyle: 'italic', fontSize: 11 }}>Revenue only</span>
                          ) : (
                            <>
                              <span style={{ color: bloomberg.text.muted }}>{formatEps(e.eps_estimate)}</span>
                              <span style={{ color: bloomberg.text.muted, margin: '0 3px' }}>/</span>
                              <Tooltip title="GAAP EPS — may differ from adjusted estimates due to one-time items" arrow>
                                <span style={{ fontWeight: 600, color: e.beat_eps ? '#4caf50' : e.beat_eps === false ? '#ef5350' : bloomberg.text.secondary }}>
                                  {formatEps(e.eps_actual)}
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                        <Box component="td" sx={tdSx}>
                          {!e.revenue_only && <BeatMissBadge beat={e.beat_eps} surprisePct={e.eps_surprise_pct} />}
                        </Box>
                        <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600, color: pctColor(e.post_earnings_move) }}>
                          {formatPct(e.post_earnings_move)}
                        </Box>
                        <Box component="td" sx={{ ...tdSx, textAlign: 'center' }}>
                          <BeatRateDisplay rate={e.beat_rate} />
                        </Box>
                        <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>
                          {formatMarketCap(e.market_cap, (e as any).currency)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {/* ── UPCOMING EARNINGS ── */}
          {filteredUpcoming.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: bloomberg.accent.orange, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Upcoming Earnings ({filteredUpcoming.length})
                </Typography>
                {upcomingTotalPages > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Button size="small" disabled={upcomingPage === 1} onClick={() => setUpcomingPage(p => p - 1)}
                      sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>‹</Button>
                    <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                      {upcomingPage}/{upcomingTotalPages} · {((upcomingPage - 1) * PAGE_SIZE) + 1}–{Math.min(upcomingPage * PAGE_SIZE, filteredUpcoming.length)} of {filteredUpcoming.length}
                    </Typography>
                    <Button size="small" disabled={upcomingPage === upcomingTotalPages} onClick={() => setUpcomingPage(p => p + 1)}
                      sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>›</Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ borderBottom: `2px solid ${bloomberg.accent.orange}`, bgcolor: bloomberg.bg.secondary }}>
                      <Box component="th" sx={thSx}>Symbol</Box>
                      <Box component="th" sx={thSx}>Company</Box>
                      <Box component="th" sx={thSx}>Sector</Box>
                      <Box component="th" sx={thSx}>Country</Box>
                      <Box component="th" sx={thSxRight}>EPS Estimate</Box>
                      <Box component="th" sx={{ ...thSx, textAlign: 'center' as const }}>Beat Rate</Box>
                      <Box component="th" sx={thSxRight}>Mkt Cap</Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {upcomingDatesForPage.map(date => (
                      <React.Fragment key={date}>
                        <Box component="tr">
                          <Box component="td" colSpan={7} sx={{
                            py: 0.5, px: 1,
                            bgcolor: isToday(date) ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                            borderLeft: isToday(date) ? `3px solid ${bloomberg.accent.orange}` : `3px solid ${bloomberg.border}`,
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: isToday(date) ? bloomberg.accent.orange : bloomberg.text.primary }}>
                                {formatDate(date)}
                              </Typography>
                              {isToday(date) && (
                                <Chip label="TODAY" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: bloomberg.accent.orange, color: '#000' }} />
                              )}
                              <Chip label={`${upcomingGrouped[date].length}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: bloomberg.accent.blueDim, color: bloomberg.accent.cyan }} />
                            </Box>
                          </Box>
                        </Box>
                        {upcomingGrouped[date].filter(e => upcomingPageSlice.has(e.symbol)).map((e: UpcomingEarningsEntry) => (
                          <Box component="tr" key={e.symbol} sx={{
                            borderBottom: `1px solid ${bloomberg.border}`,
                            '&:hover': { bgcolor: bloomberg.bg.hover },
                          }}>
                            <Box component="td" sx={tdSx}><TickerLink symbol={e.symbol} fontSize={12} /></Box>
                            <Box component="td" sx={{ ...tdSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                              {e.company_name}
                            </Box>
                            <Box component="td" sx={{ ...tdSx, fontSize: 11, color: bloomberg.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {e.sector || '—'}
                            </Box>
                            <Box component="td" sx={{ ...tdSx, fontSize: 11 }}>{(e as any).country || '—'}</Box>
                            <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>
                              {formatEps(e.eps_estimate)}
                            </Box>
                            <Box component="td" sx={{ ...tdSx, textAlign: 'center' }}>
                              <BeatRateDisplay rate={e.beat_rate} />
                            </Box>
                            <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>
                              {formatMarketCap(e.market_cap, (e as any).currency)}
                            </Box>
                          </Box>
                        ))}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default UpcomingEarningsCalendar;
